/* ===========================================================
 * UI-Primitives — Popover, IconButton, SegmentedControl, Toolbar
 * -----------------------------------------------------------
 * Diese kleinen, smarten Komponenten ersetzen die ad-hoc Buttons
 * und Chips, die den Composer überladen haben. Ziel:
 *  - Kompakter Look (Icons + Tooltips statt langer Labels)
 *  - Konsistentes Verhalten (Click-outside, Esc, Anchored)
 *  - Wiederverwendbar in allen Toolbars
 * =========================================================== */

import {
  type CSSProperties,
  type ReactNode,
  type MouseEvent as RMouseEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";

/* ======================================================================
 * IconButton — kompakter Knopf mit Icon + optionalem Label, Tooltip via title
 * ====================================================================== */

type IconButtonProps = {
  icon: ReactNode;
  label?: string;
  /** Wenn gesetzt → Label wird neben Icon gezeigt (sonst icon-only). */
  showLabel?: boolean;
  active?: boolean;
  disabled?: boolean;
  variant?: "default" | "primary" | "ghost" | "danger";
  size?: "sm" | "md";
  badge?: ReactNode;
  title?: string;
  onClick?: (e: RMouseEvent<HTMLButtonElement>) => void;
  className?: string;
  ariaLabel?: string;
  type?: "button" | "submit";
  style?: CSSProperties;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, showLabel, active, disabled, variant = "default", size = "md", badge, title, onClick, className, ariaLabel, type = "button", style },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={`ui-iconbtn v-${variant} s-${size} ${active ? "is-active" : ""} ${className ?? ""}`}
      disabled={disabled}
      title={title ?? label}
      aria-label={ariaLabel ?? label ?? title}
      onClick={onClick}
      style={style}
    >
      <span className="ui-iconbtn-icon" aria-hidden>{icon}</span>
      {label && showLabel && <span className="ui-iconbtn-label">{label}</span>}
      {badge != null && <span className="ui-iconbtn-badge">{badge}</span>}
    </button>
  );
});

/* ======================================================================
 * SegmentedControl — kompakter Toggle-Group statt Chip-Reihen
 * ====================================================================== */

type SegmentedOption<V extends string> = {
  value: V;
  label: ReactNode;
  title?: string;
};

type SegmentedProps<V extends string> = {
  value: V;
  onChange: (v: V) => void;
  options: SegmentedOption<V>[];
  size?: "sm" | "md";
  className?: string;
  ariaLabel?: string;
};

export function SegmentedControl<V extends string>({
  value, onChange, options, size = "md", className, ariaLabel,
}: SegmentedProps<V>): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`ui-segmented s-${size} ${className ?? ""}`}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={`ui-segmented-opt ${active ? "is-active" : ""}`}
            title={opt.title}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ======================================================================
 * Popover — anchor an einem Trigger-Element, schließt auf
 *   - Klick außerhalb
 *   - Esc-Taste
 *   - explizites onClose
 * ====================================================================== */

type PopoverPlacement = "bottom-start" | "bottom-end" | "bottom-center" | "top-start" | "top-end" | "top-center";

type PopoverProps = {
  trigger: (props: { open: boolean; toggle: () => void; ref: React.RefObject<HTMLButtonElement> }) => JSX.Element;
  children: ReactNode | ((api: { close: () => void }) => ReactNode);
  placement?: PopoverPlacement;
  width?: number | "auto";
  className?: string;
  /** Schließt nach jedem Item-Klick — nützlich für Menüs. */
  closeOnClickInside?: boolean;
};

export function Popover({
  trigger, children, placement = "bottom-start", width = "auto", className, closeOnClickInside = false,
}: PopoverProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; minWidth?: number }>({ left: 0, top: 0 });
  const id = useId();

  const close = useCallback((): void => setOpen(false), []);
  const toggle = useCallback((): void => setOpen((s) => !s), []);

  // Position berechnen, wenn open == true; bei Resize/Scroll re-positionieren.
  useLayoutEffect(() => {
    if (!open) return;
    const calc = (): void => {
      const t = triggerRef.current;
      const p = popRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const pw = p?.offsetWidth ?? 220;
      const ph = p?.offsetHeight ?? 200;
      let left = r.left;
      let top = r.bottom + 6;
      if (placement === "bottom-end") left = r.right - pw;
      if (placement === "bottom-center") left = r.left + r.width / 2 - pw / 2;
      if (placement.startsWith("top")) top = r.top - ph - 6;
      // Clamp in viewport
      const margin = 8;
      left = Math.max(margin, Math.min(window.innerWidth - pw - margin, left));
      top = Math.max(margin, Math.min(window.innerHeight - ph - margin, top));
      setPos({ left, top, minWidth: width === "auto" ? r.width : undefined });
    };
    calc();
    const id = window.setTimeout(calc, 0); // re-measure after content render
    window.addEventListener("scroll", calc, true);
    window.addEventListener("resize", calc);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("scroll", calc, true);
      window.removeEventListener("resize", calc);
    };
  }, [open, placement, width]);

  // Close handlers: outside click + Esc
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node | null;
      if (!t) return;
      if (popRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const renderContent = (): ReactNode => {
    if (typeof children === "function") return children({ close });
    return children;
  };

  return (
    <>
      {trigger({ open, toggle, ref: triggerRef })}
      {open && createPortal(
        <div
          ref={popRef}
          id={id}
          role="dialog"
          className={`ui-popover ${className ?? ""}`}
          style={{
            left: pos.left,
            top: pos.top,
            width: width === "auto" ? undefined : width,
            minWidth: pos.minWidth,
          }}
          onClick={(e) => {
            if (closeOnClickInside) {
              const target = e.target as HTMLElement;
              // Schließe nur, wenn der Klick auf einem Element im Popover passiert,
              // das eine Aktion repräsentiert (Button, Link, Option, Menuitem).
              if (target.closest("button, a, [role='menuitem'], [role='option']")) {
                setOpen(false);
              }
            }
          }}
        >
          {renderContent()}
        </div>,
        document.body,
      )}
    </>
  );
}

/* ======================================================================
 * Toolbar — horizontaler Container mit Trenner & gleichmäßigen Lücken
 * ====================================================================== */

type ToolbarProps = {
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
};

export function Toolbar({ children, className, ariaLabel }: ToolbarProps): JSX.Element {
  return (
    <div role="toolbar" aria-label={ariaLabel} className={`ui-toolbar ${className ?? ""}`}>
      {children}
    </div>
  );
}

export function ToolbarSep(): JSX.Element {
  return <span className="ui-toolbar-sep" aria-hidden />;
}

export function ToolbarSpacer(): JSX.Element {
  return <span className="ui-toolbar-spacer" aria-hidden />;
}

/* ======================================================================
 * MenuItem — Standard-Element für Popover-Menüs (Klick → Aktion + Close)
 * ====================================================================== */

type MenuItemProps = {
  icon?: ReactNode;
  label: ReactNode;
  hint?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  destructive?: boolean;
};

export function MenuItem({ icon, label, hint, active, disabled, onClick, destructive }: MenuItemProps): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      className={`ui-menuitem ${active ? "is-active" : ""} ${destructive ? "is-danger" : ""}`}
      disabled={disabled}
      onClick={onClick}
    >
      {icon != null && <span className="ui-menuitem-icon" aria-hidden>{icon}</span>}
      <span className="ui-menuitem-label">{label}</span>
      {hint != null && <span className="ui-menuitem-hint">{hint}</span>}
    </button>
  );
}

export function MenuSection({ title, children }: { title?: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <div className="ui-menusection">
      {title && <div className="ui-menusection-title">{title}</div>}
      <div className="ui-menusection-body">{children}</div>
    </div>
  );
}

export function MenuDivider(): JSX.Element {
  return <div className="ui-menudivider" aria-hidden />;
}
